/**
 * Autenticação Auth0 do front.
 *
 * Papel: envolve a aplicação com o `Auth0Provider`, entrega ao cliente HTTP a
 * função que obtém o token, e interrompe a árvore quando a configuração do
 * tenant não chegou ao bundle ou quando o login foi recusado.
 *
 * Como usar: o `PortaoDeSessao` deverá permanecer acima do `RouterProvider`, e
 * nunca ser movido para o `onRedirecting` do `LayoutProtegido`. O
 * `withAuthenticationRequired` observa apenas `isLoading` e `isAuthenticated`,
 * jamais `error`: depois de um login recusado ele torna a chamar
 * `loginWithRedirect`, o tenant recusa outra vez, e quem tentou entrar fica
 * preso num laço entre a aplicação e o Auth0, vendo "Verificando sessão…"
 * piscar. É por cortar a árvore antes de o layout montar que o portão impede
 * esse laço de começar.
 *
 * O fluxo é Authorization Code com PKCE, de modo que o navegador nunca vê
 * client secret. Ao voltar do Auth0, a navegação é refeita pelo roteador e não
 * por `history.replaceState`: trocar a URL por fora do react-router deixaria o
 * endereço certo e a tela errada.
 *
 * O token fica em `localStorage` e se renova por refresh token, o que cobra do
 * tenant duas contrapartidas: rotação de refresh token ligada, para que um
 * token vazado seja invalidado no primeiro uso legítimo seguinte, e expiração
 * curta no access token. Nada disso protege contra XSS; o que protege é não
 * existir `innerHTML` com template string no código.
 */
import { Auth0Provider, OAuthError, useAuth0 } from '@auth0/auth0-react';
import { useEffect, type ReactNode } from 'react';

import { configurarObtencaoDeToken } from '@/shared/api';
import { lerConfiguracaoAuth0 } from '@/shared/lib/ambiente';
import { router } from './router';

interface ProvedorAuthProps {
  children: ReactNode;
}

/**
 * Painel exibido quando falta configuração do Auth0.
 *
 * Existe para trocar a tela branca por um diagnóstico. Sem ele, um deploy sem
 * as variáveis produziria uma página vazia e um erro solto no console — e a
 * causa levaria muito mais tempo para ser encontrada do que o custo destas
 * poucas linhas.
 *
 * @param ausentes - Nomes das variáveis que faltaram.
 * @returns Tela de erro de configuração.
 */
function ConfiguracaoAusente({ ausentes }: { ausentes: readonly string[] }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-8">
      <div className="max-w-lg rounded-lg border border-border bg-card p-8">
        <h1 className="font-serif text-xl text-foreground">Configuração incompleta</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          A aplicação não tem os dados do Auth0 necessários para autenticar. Falta definir:
        </p>
        <ul className="mt-3 space-y-1 font-mono text-sm text-perigo">
          {ausentes.map((nome) => (
            <li key={nome}>{nome}</li>
          ))}
        </ul>
        <p className="mt-4 text-sm text-muted-foreground">
          Em desenvolvimento, copie <code className="font-mono">.env.example</code> para{' '}
          <code className="font-mono">.env</code> na raiz do projeto. Em produção, elas são
          variables do repositório no GitHub, lidas durante a publicação.
        </p>
      </div>
    </div>
  );
}

/** Texto exibido no painel de falha, já traduzido do erro do Auth0. */
interface DescricaoDeFalha {
  titulo: string;
  explicacao: string;
  /** Código de máquina do Auth0, ou `null` quando o erro não trouxer um. */
  codigo: string | null;
}

/**
 * Traduz a falha devolvida pelo Auth0 em texto que se possa mostrar na tela.
 *
 * O `error_description` do erro nunca é usado, e isso é deliberado: uma Action
 * do tenant pode escrever qualquer coisa nesse campo, inclusive identificando
 * quem tentou entrar, e este painel é justamente o que alguém fotografa para
 * anexar a um chamado. O que sai na tela é o texto escrito aqui, acompanhado
 * do código de máquina, que descreve a recusa sem descrever a pessoa.
 *
 * @param erro - Falha vinda do `useAuth0`, que nem sempre é um `OAuthError`:
 *   queda de rede no meio da troca de código chega como `Error` comum.
 * @returns Título, explicação e código a exibir.
 */
function descreverFalha(erro: Error): DescricaoDeFalha {
  if (!(erro instanceof OAuthError)) {
    return {
      titulo: 'Não foi possível entrar',
      explicacao:
        'A conversa com o Auth0 não chegou ao fim. Pode ter sido queda de conexão ou indisponibilidade do serviço.',
      codigo: null,
    };
  }

  switch (erro.error) {
    case 'access_denied':
    case 'unauthorized':
      return {
        titulo: 'Acesso recusado',
        explicacao:
          'Esta conta não está liberada para o sistema, ou foi bloqueada. Procure quem administra o sistema.',
        codigo: erro.error,
      };
    case 'login_required':
    case 'consent_required':
    case 'interaction_required':
      return {
        titulo: 'A sessão precisa ser refeita',
        explicacao: 'A sessão anterior deixou de valer, e entrar de novo resolve.',
        codigo: erro.error,
      };
    default:
      return {
        titulo: 'Não foi possível entrar',
        explicacao: 'O Auth0 recusou esta tentativa de login.',
        codigo: erro.error,
      };
  }
}

interface FalhaDeAutenticacaoProps {
  erro: Error;
  onTentarNovamente: () => void;
  onSair: () => void;
}

/**
 * Painel exibido no lugar da aplicação quando o login foi recusado.
 *
 * @param erro - Falha devolvida pelo `useAuth0`, traduzida por `descreverFalha`.
 * @param onTentarNovamente - Chamado pelo botão que reabre o login no Auth0.
 * @param onSair - Chamado pelo botão que encerra a sessão e limpa o cache
 *   guardado no `localStorage`.
 * @returns Tela cheia com o motivo da recusa e as duas saídas possíveis.
 */
function FalhaDeAutenticacao({ erro, onTentarNovamente, onSair }: FalhaDeAutenticacaoProps) {
  const { titulo, explicacao, codigo } = descreverFalha(erro);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-8">
      <div className="max-w-lg rounded-lg border border-border bg-card p-8">
        <h1 className="font-serif text-xl text-foreground">{titulo}</h1>
        <p className="mt-3 text-sm text-muted-foreground">{explicacao}</p>

        {codigo !== null && (
          <p className="mt-3 font-mono text-xs text-texto-fraco">{codigo}</p>
        )}

        <div className="mt-6 flex gap-4">
          <button
            type="button"
            onClick={onTentarNovamente}
            className="rounded-sm text-sm text-primary underline underline-offset-4 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          >
            Tentar de novo
          </button>
          <button
            type="button"
            onClick={onSair}
            className="rounded-sm text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          >
            Sair
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Interrompe a montagem da aplicação enquanto houver falha de login.
 *
 * Precisa envolver todo o resto da árvore, porque é a ausência do
 * `LayoutProtegido` que impede o `withAuthenticationRequired` de disparar um
 * novo redirecionamento sobre um login que o tenant acabou de recusar.
 *
 * Só falha de inicialização e de login por janela chegam aqui; a renovação
 * silenciosa de token não define `error`, de modo que o portão não fecha a
 * aplicação inteira por causa de uma requisição que falhou.
 *
 * @param children - Árvore montada apenas quando não há falha de login.
 * @returns Os filhos, ou o painel de falha no lugar deles.
 */
function PortaoDeSessao({ children }: { children: ReactNode }) {
  const { error, loginWithRedirect, logout } = useAuth0();

  /** Reabre o login; recarregar a página repetiria a falha, porque os
      parâmetros de erro continuam na URL de retorno. */
  function handleTentarNovamente() {
    void loginWithRedirect();
  }

  /** Encerra a sessão e limpa o cache — a saída para conta bloqueada. */
  function handleSair() {
    void logout({ logoutParams: { returnTo: window.location.origin } });
  }

  if (error === undefined) return children;

  return (
    <FalhaDeAutenticacao
      erro={error}
      onTentarNovamente={handleTentarNovamente}
      onSair={handleSair}
    />
  );
}

/**
 * Entrega ao cliente HTTP a função que obtém o token.
 *
 * Precisa ser um componente, e não uma chamada solta, porque
 * `getAccessTokenSilently` vem de um hook e só existe dentro da árvore do
 * `Auth0Provider`. O registro acontece em efeito para não rodar durante a
 * renderização, onde escrever em estado de módulo é efeito colateral.
 *
 * @param children - Árvore que passa a poder chamar a API autenticada.
 * @returns Os filhos, sem envolvê-los em nenhum elemento.
 */
function RegistroDeToken({ children }: { children: ReactNode }) {
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    configurarObtencaoDeToken(() => getAccessTokenSilently());
  }, [getAccessTokenSilently]);

  return children;
}

/**
 * Envolve a aplicação com a autenticação do Auth0.
 *
 * Quem monta este componente é o `Providers`, por fora do `QueryClientProvider`
 * e por fora do `RouterProvider`. A ordem não é arbitrária: toda consulta
 * precisa de token, então o provedor que o fornece deverá estar montado antes
 * de qualquer requisição partir, e o `PortaoDeSessao` precisa conseguir
 * substituir a aplicação inteira antes de o roteador montar a tela protegida.
 *
 * Faltando variável de ambiente, o `Auth0Provider` nem chega a ser criado e a
 * árvore é trocada pelo painel de configuração — o roteador junto, de modo que
 * nenhuma rota responde enquanto o `.env` estiver incompleto.
 *
 * @param children - Árvore que passa a ter a sessão disponível pelo `useAuth0`.
 * @returns Os filhos dentro do provedor, ou o painel que lista as variáveis
 *   ausentes.
 */
export function ProvedorAuth({ children }: ProvedorAuthProps) {
  const resultado = lerConfiguracaoAuth0();

  if (!resultado.ok) return <ConfiguracaoAusente ausentes={resultado.ausentes} />;

  const { dominio, clientId, audience } = resultado.configuracao;

  return (
    <Auth0Provider
      domain={dominio}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: window.location.origin,
        // Sem `audience`, o Auth0 devolve um token opaco que serve para ler o
        // perfil e nada mais. É o `audience` que faz o token sair como JWT
        // destinado à nossa API — e é ele que a Function confere.
        audience,
      }}
      useRefreshTokens
      cacheLocation="localstorage"
      onRedirectCallback={(estado) => {
        void router.navigate(estado?.returnTo ?? '/', { replace: true });
      }}
    >
      <PortaoDeSessao>
        <RegistroDeToken>{children}</RegistroDeToken>
      </PortaoDeSessao>
    </Auth0Provider>
  );
}
